import { inject, Injectable } from '@angular/core';
import { CentreNotificationControllerApi } from '../../api/services/centre-notification-controller-api';
import { UserService } from '../../shared/providers/auth/user.service';
import { GetNotificationNonSupprimeesByIntervenant$Params } from '../../api/fn/centre-notification-controller/get-notification-non-supprimees-by-intervenant';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { CentreNotificationResponse } from '../../api/models/centre-notification-response';
import { NetworkProvider } from '../../shared/providers/network/network';
import { LoggerWrapper } from '../../shared/providers/logger/logger-wrapper.service';
import { LoggerFactory } from '../../shared/providers/logger/logger-factory.service';
import { MarkNotificationAsRead$Params } from '../../api/fn/centre-notification-controller/mark-notification-as-read';
import { MarkNotificationAsDeleted$Params } from '../../api/fn/centre-notification-controller/mark-notification-as-deleted';

@Injectable({ providedIn: 'root' })
export class CentreNotificationService {
  private readonly networkProvider = inject(NetworkProvider);
  private readonly userService = inject(UserService);
  private readonly centreNotificationControllerApi = inject(CentreNotificationControllerApi);
  private readonly logger: LoggerWrapper = inject(LoggerFactory).getInstance(this, true);

  public centreNotificationData$: BehaviorSubject<CentreNotificationResponse> = new BehaviorSubject(null);
  public loadingError$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  async chargerDonnesCentreNotification() {
    if (await this.networkProvider.isReallyConnected()) {
      const params: GetNotificationNonSupprimeesByIntervenant$Params = {
        id_intervenant: this.userService.getId(),
      };
      firstValueFrom(this.centreNotificationControllerApi.getNotificationNonSupprimeesByIntervenant(params))
        .then((result) => {
          this.centreNotificationData$.next(result);
          this.loadingError$.next(false);
        })
        .catch((error) => {
          this.logger.error(error, 'Erreur de récupération des notifications');
          this.loadingError$.next(true);
        });
    }
  }

  async markAsRead(idNotificationIntervenant: string) {
    const params: MarkNotificationAsRead$Params = {
      id_notification_intervenant: idNotificationIntervenant,
    };

    return firstValueFrom(this.centreNotificationControllerApi.markNotificationAsRead(params)).then((response) => {
      const currentState = this.centreNotificationData$.getValue();

      if (currentState && currentState.notifications) {
        const updatedNotifications = currentState.notifications.map((notif) =>
          notif.idNotifIntervenant === idNotificationIntervenant ? { ...notif, isRead: true } : notif
        );

        this.centreNotificationData$.next({
          ...currentState,
          notifications: updatedNotifications,
        });
      }

      return response;
    });
  }

  async markAsDeleted(idNotificationIntervenant: string) {
    const params: MarkNotificationAsDeleted$Params = {
      id_notification_intervenant: idNotificationIntervenant,
    };

    return firstValueFrom(this.centreNotificationControllerApi.markNotificationAsDeleted(params)).then((response) => {
      const currentState = this.centreNotificationData$.getValue();

      if (currentState && currentState.notifications) {
        const filteredNotifications = currentState.notifications.filter(
          (notif) => notif.idNotifIntervenant !== idNotificationIntervenant
        );

        this.centreNotificationData$.next({
          ...currentState,
          notifications: filteredNotifications,
        });
      }

      return response;
    });
  }
}
